// Ahorros.jsx
import React, { useState, useEffect, useContext, useCallback } from 'react';
import {
  Building2, Download, Upload, Save, TrendingDown,
  Package, ChevronDown, ChevronRight, AlertCircle, CheckCircle,
  BarChart2, DollarSign, Percent, X, ArrowDown, ArrowUp,
  Info, ExternalLink, RefreshCw, Filter,
  TrendingUp, Award, History, Users, Calendar, ShoppingCart, Tag,
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid,
  Cell, PieChart, Pie, Legend, LineChart, Line,
} from 'recharts';
import { AuthContext } from '../App';
import SearchInput from '../components/SearchInput';

const API_URL = process.env.REACT_APP_API_URL;

// ─── Helpers ────────────────────────────────────────────────────────────────
const fmt = (v) => {
  if (v == null || v === '' || isNaN(v)) return '—';
  const n = parseFloat(v);
  return n.toFixed(4).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');
};
const fmtMoney = (v) =>
  v != null && v !== '' && !isNaN(v) ? `${fmt(v)} €` : '—';
const fmtPct = (v) =>
  v != null && v !== '' && !isNaN(v) ? `${parseFloat(v).toFixed(2)}%` : '—';
const fmtBigMoney = (v) => {
  if (v == null || isNaN(v)) return '—';
  const n = parseFloat(v);
  if (n >= 1000) return `${(n / 1000).toFixed(2)}k €`;
  return `${n.toFixed(2)} €`;
};
const fmtDate = (d) => {
  if (!d) return '—';
  const [y, m] = String(d).split('-');
  const meses = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];
  return m ? `${meses[parseInt(m) - 1]} ${y}` : d;
};

// ─── Colores ─────────────────────────────────────────────────────────────────
const PRIMARY      = '#3cbf9a';
const PRIMARY_DARK = '#2da080';
const PRIMARY_LITE = 'rgba(60,191,154,0.08)';
const RANGO_COLORS = {
  'Sin configurar': '#9ca3af',
  '0% o menos':    '#ef4444',
  '1% - 10%':      '#f59e0b',
  '11% - 20%':     PRIMARY,
  '21% - 30%':     '#059669',
  'Más del 30%':   '#047857',
};

// ─── Tooltip personalizado Recharts ─────────────────────────────────────────
const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-gray-800 text-white text-xs px-3 py-2 rounded-lg shadow-lg">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color || '#fff' }}>
          {p.name}: {formatter ? formatter(p.value) : p.value}
        </p>
      ))}
    </div>
  );
};

export default function Ahorros() {
  const { user } = useContext(AuthContext);

  const [matrices, setMatrices]         = useState([]);
  const [selectedMatriz, setSelected]   = useState(null);
  const [clientes, setClientes]         = useState([]);
  const [productos, setProductos]       = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);

  const [loadingMatrices, setLoadingMatrices] = useState(false);
  const [loadingProds, setLoadingProds]       = useState(false);
  const [saving, setSaving]                   = useState(false);
  const [error, setError]                     = useState(null);

  const [activeTab, setActiveTab]             = useState('precios');
  const [searchFilter, setSearchFilter]       = useState('');
  const [matrizSearch, setMatrizSearch]       = useState('');

  const [editedPrices, setEditedPrices]       = useState({});
  const [expandedRows, setExpandedRows]       = useState(new Set());
  const [filterDiff, setFilterDiff]           = useState(false);
  const [filterNoPrev, setFilterNoPrev]       = useState(false);

  const [uploadingFile, setUploadingFile]     = useState(false);
  const [uploadMsg, setUploadMsg]             = useState('');
  const [uploadErr, setUploadErr]             = useState('');
  const [isDragOver, setIsDragOver]           = useState(false);

  // histórico
  const [historicoData, setHistoricoData]       = useState(null);
  const [loadingHistorico, setLoadingHistorico] = useState(false);
  const [clientesSeleccionados, setClientesSel] = useState(new Set());
  const [histSearch, setHistSearch]             = useState('');
  const [histOrden, setHistOrden]               = useState('ahorro_total');
  const [histFiltroProveedor, setHistProveedor] = useState('');
  const [histFechaDesde, setHistFechaDesde]     = useState('');
  const [histFechaHasta, setHistFechaHasta]     = useState('');
  const [proveedoresDisponibles, setProvDisp]   = useState([]); // lista estable para el select

  const hasChanges = Object.keys(editedPrices).length > 0;

  // ── Carga inicial ────────────────────────────────────────────────────────
  useEffect(() => { window.scrollTo(0, 0); loadMatrices(); }, []);

  const loadMatrices = async () => {
    try {
      setLoadingMatrices(true);
      const r = await fetch(`${API_URL}/api/ahorros/matrices`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setMatrices(d.matrices || []);
    } catch { setError('Error al cargar las matrices'); }
    finally   { setLoadingMatrices(false); }
  };

  const loadProductos = async (matrizId) => {
    try {
      setLoadingProds(true); setError(null);
      const r = await fetch(`${API_URL}/api/ahorros/${matrizId}/productos`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setProductos(d.productos || []);
      setClientes(d.clientes   || []);
      setEditedPrices({});
    } catch { setError('Error al cargar productos'); setProductos([]); }
    finally   { setLoadingProds(false); }
  };

  const loadEstadisticas = async (matrizId) => {
    try {
      const r = await fetch(`${API_URL}/api/ahorros/${matrizId}/estadisticas`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!r.ok) return;
      setEstadisticas(await r.json());
    } catch (e) { console.error(e); }
  };

  const loadHistorico = useCallback(async (matrizId, selIds, proveedor, fechaDesde, fechaHasta) => {
    try {
      setLoadingHistorico(true);
      const p = new URLSearchParams();
      if (selIds?.size > 0)  p.set('clientes',    [...selIds].join(','));
      if (proveedor)         p.set('proveedor',   proveedor);
      if (fechaDesde)        p.set('fecha_desde', fechaDesde);
      if (fechaHasta)        p.set('fecha_hasta', fechaHasta);
      const qs = p.toString() ? `?${p.toString()}` : '';
      const r = await fetch(`${API_URL}/api/ahorros/${matrizId}/ahorro-historico${qs}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!r.ok) return;
      const data = await r.json();
      setHistoricoData(data);
      // Actualizar lista de proveedores disponibles solo la primera vez (sin filtros activos)
      if (!proveedor && !fechaDesde && !fechaHasta && selIds?.size === 0) {
        const provs = [...new Set(
          (data.productos || []).map(p => p.proveedor).filter(Boolean).flatMap(s => s.split(', '))
        )].sort();
        setProvDisp(provs);
      }
    } catch (e) { console.error(e); }
    finally { setLoadingHistorico(false); }
  }, []);

  const handleMatrizSelect = (m) => {
    setSelected(m);
    setEditedPrices({});
    setExpandedRows(new Set());
    setSearchFilter('');
    setActiveTab('precios');
    setUploadMsg(''); setUploadErr('');
    setHistoricoData(null);
    setClientesSel(new Set());
    setHistProveedor('');
    setHistFechaDesde('');
    setHistFechaHasta('');
    setProvDisp([]);
    loadProductos(m.id);
    loadEstadisticas(m.id);
  };

  const handleClienteToggle = (id) =>
    setClientesSel(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  useEffect(() => {
    if (activeTab === 'historico' && selectedMatriz)
      loadHistorico(selectedMatriz.id, clientesSeleccionados, histFiltroProveedor, histFechaDesde, histFechaHasta);
  }, [activeTab, clientesSeleccionados, histFiltroProveedor, histFechaDesde, histFechaHasta, selectedMatriz, loadHistorico]);

  // ── Edición ──────────────────────────────────────────────────────────────
  const editPrice = (pid, field, value) =>
    setEditedPrices(prev => ({ ...prev, [pid]: { ...(prev[pid] || {}), [field]: value } }));

  const getVal = (prod, field) => {
    if (editedPrices[prod.id]?.[field] !== undefined) return editedPrices[prod.id][field];
    if (field === 'precio')          return prod.precio_matriz          ?? '';
    if (field === 'precio_anterior') return prod.precio_anterior_matriz ?? '';
    return '';
  };

  const calcAhorro = (prod) => {
    const p  = parseFloat(editedPrices[prod.id]?.precio          ?? prod.precio_matriz);
    const pa = parseFloat(editedPrices[prod.id]?.precio_anterior ?? prod.precio_anterior_matriz);
    return (!isNaN(p) && !isNaN(pa) && pa > 0) ? Math.max(0, pa - p) : null;
  };

  const calcAhorroPct = (prod) => {
    const p  = parseFloat(editedPrices[prod.id]?.precio          ?? prod.precio_matriz);
    const pa = parseFloat(editedPrices[prod.id]?.precio_anterior ?? prod.precio_anterior_matriz);
    return (!isNaN(p) && !isNaN(pa) && pa > 0) ? ((pa - p) / pa) * 100 : null;
  };

  // ── Guardar ──────────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!hasChanges || !selectedMatriz) return;
    const updates = Object.entries(editedPrices).map(([id, vals]) => {
      const prod = productos.find(p => p.id === parseInt(id));
      return { producto_id: parseInt(id),
        precio: vals.precio ?? prod?.precio_matriz ?? 0,
        precio_anterior: vals.precio_anterior ?? prod?.precio_anterior_matriz ?? null };
    });
    try {
      setSaving(true);
      const r = await fetch(`${API_URL}/api/ahorros/${selectedMatriz.id}/precios/bulk`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message);
      await loadProductos(selectedMatriz.id);
      await loadEstadisticas(selectedMatriz.id);
      setEditedPrices({});
      alert(`✅ ${d.message}`);
    } catch (e) { alert(`Error: ${e.message}`); }
    finally { setSaving(false); }
  };

  // ── Export / Import ──────────────────────────────────────────────────────
  const handleExport = async () => {
    if (!selectedMatriz) return;
    const r = await fetch(`${API_URL}/api/ahorros/${selectedMatriz.id}/export`, {
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
    });
    if (!r.ok) { alert('Error al exportar'); return; }
    const blob = await r.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `precios_${selectedMatriz.razon_social.replace(/[^a-zA-Z0-9]/g, '_')}.xlsx`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  const handleFileUpload = async (file) => {
    if (!selectedMatriz) { setUploadErr('Selecciona una matriz primero'); return; }
    if (!['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
          'application/vnd.ms-excel'].includes(file.type)) {
      setUploadErr('Solo archivos Excel (.xlsx, .xls)'); return;
    }
    try {
      setUploadingFile(true); setUploadErr(''); setUploadMsg('');
      const fd = new FormData(); fd.append('file', file);
      const r  = await fetch(`${API_URL}/api/ahorros/${selectedMatriz.id}/import`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: fd,
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.message);
      setUploadMsg(`✅ ${d.message}`);
      await loadProductos(selectedMatriz.id);
      await loadEstadisticas(selectedMatriz.id);
    } catch (e) { setUploadErr(e.message); }
    finally { setUploadingFile(false); }
  };

  const onDrop = (e) => {
    e.preventDefault(); setIsDragOver(false);
    const f = [...e.dataTransfer.files][0];
    if (f) handleFileUpload(f);
  };

  const toggleRow = (id) =>
    setExpandedRows(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  // ── Filtros ──────────────────────────────────────────────────────────────
  const filtered = productos.filter(p => {
    const s = searchFilter.toLowerCase();
    if (s && !(p.nombre?.toLowerCase().includes(s) || p.ref_proveedor?.toLowerCase().includes(s) ||
               p.proveedores?.toLowerCase().includes(s) || p.familia?.toLowerCase().includes(s))) return false;
    if (filterDiff   && !p.hay_precios_diferentes)  return false;
    if (filterNoPrev && p.precio_anterior_matriz)    return false;
    return true;
  });

  const filteredMatrices = matrices.filter(m =>
    !matrizSearch ||
    m.razon_social?.toLowerCase().includes(matrizSearch.toLowerCase()) ||
    m.alias?.toLowerCase().includes(matrizSearch.toLowerCase())
  );

  const histProductosFiltrados = (historicoData?.productos || [])
    .filter(p => {
      const s = histSearch.toLowerCase();
      return !s || p.nombre?.toLowerCase().includes(s) || p.proveedor?.toLowerCase().includes(s);
    })
    .sort((a, b) => parseFloat(b[histOrden] || 0) - parseFloat(a[histOrden] || 0));

  const clientesFiltroTexto = clientesSeleccionados.size === 0
    ? 'Todas las clínicas'
    : `${clientesSeleccionados.size} clínica${clientesSeleccionados.size > 1 ? 's' : ''} seleccionada${clientesSeleccionados.size > 1 ? 's' : ''}`;

  // ────────────────────────────────────────────────────────────────────────
  // RENDER — layout 100% viewport con scroll interno por sección
  // ────────────────────────────────────────────────────────────────────────
  return (
    // Contenedor raíz: ocupa todo el viewport menos el header (top-16)
    <div className="fixed inset-0 bg-gradient-to-br from-gray-50 to-gray-100 flex overflow-hidden" style={{ top: 64 }}>

      {/* ══ SIDEBAR MATRICES ════════════════════════════════════════════════ */}
      <aside className="w-72 bg-white shadow-lg border-r border-gray-200 flex-shrink-0 flex flex-col overflow-hidden">

        {/* Cabecera fija */}
        <div className="p-5 border-b border-gray-100 flex-shrink-0">
          <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <TrendingDown size={22} style={{ color: PRIMARY }} />
            Análisis de Ahorros
          </h2>
          <p className="text-xs text-gray-500 mt-1">Selecciona una clínica matriz</p>
          <SearchInput
            className="mt-3"
            value={matrizSearch}
            onChange={e => setMatrizSearch(e.target.value)}
            placeholder="Buscar matriz..."
          />
        </div>

        {/* Lista con scroll propio */}
        <div className="flex-1 overflow-y-auto p-3 min-h-0">
          {loadingMatrices ? (
            <div className="text-center py-10 text-sm text-gray-400 animate-pulse">Cargando matrices...</div>
          ) : filteredMatrices.length === 0 ? (
            <div className="text-center py-10 text-gray-400">
              <Building2 size={36} className="mx-auto mb-2 opacity-30" />
              <p className="text-sm">Sin matrices disponibles</p>
            </div>
          ) : (
            <div className="space-y-2">
              {filteredMatrices.map(m => {
                const active = selectedMatriz?.id === m.id;
                return (
                  <button key={m.id} onClick={() => handleMatrizSelect(m)}
                    className="w-full text-left p-3 rounded-xl border transition-all duration-200"
                    style={active ? { borderColor: PRIMARY, backgroundColor: PRIMARY_LITE } : { borderColor: '#e5e7eb', backgroundColor: 'white' }}
                    onMouseEnter={e => { if (!active) e.currentTarget.style.borderColor = '#d1d5db'; }}
                    onMouseLeave={e => { if (!active) e.currentTarget.style.borderColor = '#e5e7eb'; }}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="w-2 h-2 rounded-full flex-shrink-0"
                            style={{ backgroundColor: active ? PRIMARY : '#9ca3af' }} />
                          <span className="font-semibold text-sm text-gray-800 truncate">{m.razon_social}</span>
                        </div>
                        {m.alias && <p className="text-xs text-gray-400 mt-0.5 ml-4 truncate">{m.alias}</p>}
                        <div className="flex items-center gap-3 mt-1.5 ml-4">
                          <span className="text-xs text-gray-500">
                            {parseInt(m.num_asociadas) > 0 ? `${m.num_asociadas} asoc.` : 'Sin asoc.'}
                          </span>
                          <span className="text-xs font-medium" style={{ color: PRIMARY }}>{m.num_productos} prods.</span>
                          {parseFloat(m.ahorro_total) > 0 && (
                            <span className="text-xs text-green-600 font-medium">{parseFloat(m.ahorro_total).toFixed(2)}€</span>
                          )}
                        </div>
                      </div>
                      {active && <CheckCircle size={16} style={{ color: PRIMARY }} className="ml-1 mt-0.5 flex-shrink-0" />}
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer fijo */}
        <div className="p-3 border-t border-gray-100 flex-shrink-0">
          <button onClick={loadMatrices}
            className="w-full flex items-center justify-center gap-2 py-2 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors">
            <RefreshCw size={14} />Actualizar lista
          </button>
        </div>
      </aside>

      {/* ══ ÁREA PRINCIPAL ══════════════════════════════════════════════════ */}
      {/* flex col para que header + tabs usen la altura disponible */}
      <div className="flex-1 flex flex-col overflow-hidden">

        {!selectedMatriz ? (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center max-w-md p-12 bg-white rounded-2xl shadow-lg border border-gray-100">
              <div className="w-20 h-20 mx-auto mb-5 rounded-full flex items-center justify-center"
                style={{ backgroundColor: PRIMARY_LITE }}>
                <TrendingDown size={40} style={{ color: PRIMARY }} />
              </div>
              <h2 className="text-2xl font-bold text-gray-800 mb-3">Análisis de Precios y Ahorros</h2>
              <p className="text-gray-500 leading-relaxed">
                Selecciona una clínica matriz en el panel izquierdo para ver el listado de productos,
                gestionar precios anteriores y analizar el ahorro obtenido.
              </p>
              {error && <div className="mt-4 p-3 bg-red-50 text-red-600 rounded-lg text-sm">{error}</div>}
            </div>
          </div>
        ) : (
          // Contenedor que se adapta a la altura disponible
          <div className="flex-1 flex flex-col overflow-hidden p-5 gap-4 min-h-0">

            {/* ── Header grupo — altura fija ─────────────────────────────── */}
            <div className="rounded-xl shadow-md px-5 py-4 text-white flex-shrink-0"
              style={{ backgroundColor: PRIMARY }}>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <div className="p-2 rounded-lg" style={{ backgroundColor: 'rgba(255,255,255,0.2)' }}>
                    <Building2 size={24} />
                  </div>
                  <div>
                    <h1 className="text-lg font-bold leading-tight">{selectedMatriz.razon_social}</h1>
                    {selectedMatriz.alias && <p className="opacity-80 text-xs">{selectedMatriz.alias}</p>}
                    <div className="flex items-center gap-4 mt-0.5">
                      <span className="opacity-70 text-xs">{clientes.length} clínicas · {productos.length} productos</span>

                    </div>
                  </div>
                </div>
                {hasChanges && (
                  <div className="flex items-center gap-2">
                    <span className="bg-yellow-400 text-yellow-900 text-xs font-bold px-3 py-1.5 rounded-full">
                      {Object.keys(editedPrices).length} cambios sin guardar
                    </span>
                    <button onClick={() => setEditedPrices({})}
                      className="text-xs text-white opacity-70 hover:opacity-100 px-2 py-1 border border-white border-opacity-40 rounded-lg">
                      Descartar
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* ── Card de tabs — ocupa el resto de la altura ────────────── */}
            <div className="flex-1 flex flex-col bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden min-h-0">

              {/* Tab bar — fijo */}
              <div className="flex border-b border-gray-200 bg-gray-50 flex-shrink-0">
                {[
                  { key: 'precios',      label: 'Precios y Ahorros', icon: <DollarSign size={15} /> },
                  { key: 'estadisticas', label: 'Estadísticas',       icon: <BarChart2  size={15} /> },
                  { key: 'historico',    label: 'Ahorro Histórico',   icon: <History    size={15} /> },
                ].map(t => (
                  <button key={t.key} onClick={() => setActiveTab(t.key)}
                    className="flex items-center gap-2 px-6 py-3.5 text-sm font-medium border-b-2 transition-colors"
                    style={activeTab === t.key
                      ? { borderColor: PRIMARY, color: PRIMARY, backgroundColor: 'white' }
                      : { borderColor: 'transparent', color: '#6b7280' }}>
                    {t.icon}{t.label}
                  </button>
                ))}
              </div>

              {/* ══ TAB PRECIOS ══ scroll interno */}
              {activeTab === 'precios' && (
                <div className="flex-1 flex flex-col overflow-hidden min-h-0">

                  {/* Toolbar fija */}
                  <div className="p-4 border-b border-gray-100 bg-gray-50 space-y-2 flex-shrink-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <SearchInput
                        className="flex-1 min-w-52"
                        value={searchFilter}
                        onChange={e => setSearchFilter(e.target.value)}
                        onClear={() => setSearchFilter('')}
                        placeholder="Buscar por nombre, referencia, proveedor..."
                      />
                      <button onClick={() => setFilterDiff(!filterDiff)}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${filterDiff ? 'bg-amber-100 text-amber-700 border-amber-300' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                        <AlertCircle size={13} />Con diferencias
                      </button>
                      <button onClick={() => setFilterNoPrev(!filterNoPrev)}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${filterNoPrev ? 'bg-blue-100 text-blue-700 border-blue-300' : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'}`}>
                        <Info size={13} />Sin precio anterior
                      </button>
                      <div className="flex-1" />
                      <button onClick={handleExport}
                        className="flex items-center gap-1.5 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
                        <Download size={15} />Exportar
                      </button>
                      <label
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm cursor-pointer border transition-colors ${isDragOver ? 'bg-green-50 border-green-400 text-green-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}
                        onDragOver={e => { e.preventDefault(); setIsDragOver(true); }}
                        onDragLeave={e => { e.preventDefault(); setIsDragOver(false); }}
                        onDrop={onDrop}>
                        <input type="file" className="hidden" accept=".xlsx,.xls"
                          onChange={e => { if (e.target.files[0]) handleFileUpload(e.target.files[0]); e.target.value = ''; }}
                          disabled={uploadingFile} />
                        {uploadingFile
                          ? <><div className="w-3.5 h-3.5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />Importando...</>
                          : <><Upload size={15} />Importar</>}
                      </label>
                      {hasChanges && (
                        <button onClick={handleSave} disabled={saving}
                          className="flex items-center gap-1.5 px-4 py-2 text-white rounded-lg text-sm font-semibold disabled:opacity-60"
                          style={{ backgroundColor: PRIMARY }}
                          onMouseEnter={e => !saving && (e.currentTarget.style.backgroundColor = PRIMARY_DARK)}
                          onMouseLeave={e => !saving && (e.currentTarget.style.backgroundColor = PRIMARY)}>
                          {saving
                            ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />Guardando...</>
                            : <><Save size={15} />Guardar ({Object.keys(editedPrices).length})</>}
                        </button>
                      )}
                    </div>
                    {(uploadMsg || uploadErr) && (
                      <div className={`p-2.5 rounded-lg text-sm flex items-center justify-between ${uploadMsg ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
                        <span>{uploadMsg || uploadErr}</span>
                        <button onClick={() => { setUploadMsg(''); setUploadErr(''); }}><X size={13} /></button>
                      </div>
                    )}
                  </div>

                  {/* Status bar fija */}
                  <div className="px-4 py-2 bg-white border-b border-gray-100 flex flex-wrap items-center gap-4 text-xs text-gray-500 flex-shrink-0">
                    <span className="font-medium text-gray-700">{filtered.length} productos</span>
                    {filtered.filter(p => p.hay_precios_diferentes).length > 0 && (
                      <span className="text-amber-600 flex items-center gap-1">
                        <AlertCircle size={12} />{filtered.filter(p => p.hay_precios_diferentes).length} con precios distintos
                      </span>
                    )}
                    {filtered.filter(p => !p.precio_anterior_matriz && p.tiene_precio_matriz).length > 0 && (
                      <span className="text-blue-500 flex items-center gap-1">
                        <Info size={12} />{filtered.filter(p => !p.precio_anterior_matriz && p.tiene_precio_matriz).length} sin precio anterior
                      </span>
                    )}
                    {hasChanges && <span className="text-yellow-600 font-semibold ml-auto">{Object.keys(editedPrices).length} cambios pendientes</span>}
                  </div>

                  {/* Tabla — scroll propio */}
                  <div className="flex-1 overflow-auto min-h-0">
                    {loadingProds ? (
                      <div className="p-12 text-center">
                        <div className="w-10 h-10 border-4 border-gray-200 rounded-full animate-spin mx-auto mb-3" style={{ borderTopColor: PRIMARY }} />
                        <p className="text-gray-500">Cargando productos...</p>
                      </div>
                    ) : (
                      <table className="w-full min-w-[920px] text-sm">
                        <thead className="sticky top-0 z-10">
                          <tr className="bg-gray-50 border-b border-gray-200 text-left">
                            <th className="w-8 px-3 py-3"></th>
                            <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Producto</th>
                            <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide">Proveedor</th>
                            <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide text-right">Precio Actual<div className="font-normal normal-case text-gray-400">(editable)</div></th>
                            <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide text-right">Precio Anterior<div className="font-normal normal-case text-gray-400">(antes de CS)</div></th>
                            <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide text-right">Ahorro €</th>
                            <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide text-right">Ahorro %</th>
                            <th className="px-4 py-3 text-xs font-semibold text-gray-600 uppercase tracking-wide text-center">Clínicas</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {filtered.length === 0 ? (
                            <tr>
                              <td colSpan={8} className="px-4 py-14 text-center text-gray-400">
                                <Package size={36} className="mx-auto mb-3 opacity-30" />
                                <p className="font-medium">No se encontraron productos</p>
                                {(filterDiff || filterNoPrev || searchFilter) && (
                                  <button onClick={() => { setSearchFilter(''); setFilterDiff(false); setFilterNoPrev(false); }}
                                    className="mt-2 text-xs underline">Limpiar filtros</button>
                                )}
                              </td>
                            </tr>
                          ) : filtered.map(prod => {
                            const isEdited   = !!editedPrices[prod.id];
                            const isExpanded = expandedRows.has(prod.id);
                            const ahorro     = calcAhorro(prod);
                            const ahorroPct  = calcAhorroPct(prod);
                            const showExpand = prod.hay_precios_diferentes || !prod.tiene_precio_matriz || clientes.length > 1;
                            return (
                              <React.Fragment key={prod.id}>
                                <tr className={`hover:bg-gray-50 transition-colors ${isEdited ? 'bg-yellow-50 hover:bg-yellow-50' : ''}`}>
                                  <td className="px-3 py-3 w-8">
                                    {showExpand && (
                                      <button onClick={() => toggleRow(prod.id)} className="text-gray-400 hover:text-gray-600">
                                        {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                                      </button>
                                    )}
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="font-medium text-gray-800 leading-tight">{prod.nombre}</div>
                                    <div className="text-xs text-gray-400 mt-0.5 flex items-center gap-2">
                                      {prod.ref_proveedor && <span>{prod.ref_proveedor}</span>}
                                      {prod.familia && <span className="opacity-70">{prod.familia}</span>}
                                      {prod.tipo_iva && <span className="px-1 bg-gray-100 text-gray-500 rounded text-[10px]">IVA {prod.tipo_iva}%</span>}
                                    </div>
                                    {!prod.tiene_precio_matriz && (
                                      <span className="mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 bg-orange-50 text-orange-600 text-[10px] font-medium rounded">
                                        <AlertCircle size={9} />Solo en asociadas
                                      </span>
                                    )}
                                    {isEdited && <span className="mt-1 ml-1 inline-flex items-center gap-1 px-1.5 py-0.5 bg-yellow-100 text-yellow-700 text-[10px] font-medium rounded">Modificado</span>}
                                  </td>
                                  <td className="px-4 py-3"><span className="text-gray-600 text-xs">{prod.proveedores || '—'}</span></td>
                                  <td className="px-4 py-3">
                                    <div className="flex justify-end">
                                      {prod.tiene_precio_matriz ? (
                                        <input type="number" step="0.0001" min="0" value={getVal(prod, 'precio')}
                                          onChange={e => editPrice(prod.id, 'precio', e.target.value === '' ? null : e.target.value)}
                                          className="w-28 text-right px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none"
                                          onFocus={e => (e.target.style.borderColor = PRIMARY)}
                                          onBlur={e  => (e.target.style.borderColor = '#e5e7eb')} />
                                      ) : <span className="text-gray-300">—</span>}
                                    </div>
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex justify-end">
                                      <input type="number" step="0.0001" min="0" value={getVal(prod, 'precio_anterior')}
                                        onChange={e => editPrice(prod.id, 'precio_anterior', e.target.value === '' ? null : e.target.value)}
                                        placeholder="—"
                                        className="w-28 text-right px-2 py-1.5 border border-gray-200 rounded-lg text-sm focus:outline-none placeholder-gray-300"
                                        onFocus={e => (e.target.style.borderColor = PRIMARY)}
                                        onBlur={e  => (e.target.style.borderColor = '#e5e7eb')} />
                                    </div>
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    {ahorro !== null ? (
                                      <span className={`text-sm font-semibold ${ahorro > 0 ? 'text-green-600' : ahorro < 0 ? 'text-red-500' : 'text-gray-400'}`}>
                                        {ahorro >= 0 ? '+' : ''}{ahorro.toFixed(4).replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '')} €
                                      </span>
                                    ) : <span className="text-gray-300">—</span>}
                                  </td>
                                  <td className="px-4 py-3 text-right">
                                    {ahorroPct !== null ? (
                                      <span className={`inline-flex items-center gap-0.5 text-xs font-bold px-2 py-0.5 rounded-full ${ahorroPct > 0 ? 'bg-green-100 text-green-700' : ahorroPct < 0 ? 'bg-red-100 text-red-600' : 'bg-gray-100 text-gray-500'}`}>
                                        {ahorroPct > 0 ? <ArrowDown size={10} /> : ahorroPct < 0 ? <ArrowUp size={10} /> : null}
                                        {Math.abs(ahorroPct).toFixed(2)}%
                                      </span>
                                    ) : <span className="text-gray-300">—</span>}
                                  </td>
                                  <td className="px-4 py-3">
                                    <div className="flex items-center justify-center gap-1.5 flex-wrap">
                                      <span className="text-xs text-gray-500">{prod.clientes_con_precio}/{prod.clientes_total}</span>
                                      {prod.hay_precios_diferentes && (
                                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-700">
                                          <AlertCircle size={10} />Dif.
                                        </span>
                                      )}
                                      {prod.clientes_con_precio < prod.clientes_total && (
                                        <span className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-orange-100 text-orange-600">Parcial</span>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                                {isExpanded && (
                                  <tr>
                                    <td colSpan={8} className="px-4 py-3 bg-gray-50 border-b border-gray-100">
                                      <div className="ml-7">
                                        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Precios por clínica — {prod.nombre}</p>
                                        <div className="flex flex-wrap gap-2.5">
                                          {prod.precios_por_cliente.map(pc => {
                                            const dif = pc.tiene_precio && prod.tiene_precio_matriz && pc.precio !== null && prod.precio_matriz !== null &&
                                              parseFloat(pc.precio).toFixed(4) !== parseFloat(prod.precio_matriz).toFixed(4);
                                            return (
                                              <div key={pc.cliente_id}
                                                className={`bg-white border rounded-xl px-3.5 py-2.5 text-xs min-w-[160px] ${!pc.tiene_precio ? 'opacity-50 border-dashed border-gray-300' : dif ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}>
                                                <div className="flex items-center gap-1.5 mb-1.5 font-medium text-gray-700">
                                                  <div className="w-2 h-2 rounded-full" style={{ backgroundColor: pc.es_matriz ? PRIMARY : '#9ca3af' }} />
                                                  <span className="truncate max-w-[130px]">{pc.razon_social}</span>
                                                  {pc.es_matriz && <span className="text-[9px] px-1 rounded font-bold" style={{ backgroundColor: PRIMARY_LITE, color: PRIMARY }}>MATRIZ</span>}
                                                </div>
                                                {pc.tiene_precio ? (
                                                  <div className="space-y-0.5 text-gray-600">
                                                    <div>Actual: <span className="font-semibold text-gray-800">{fmt(pc.precio)} €</span>
                                                      {dif && <span className="ml-1 text-amber-600 font-semibold text-[10px]">≠ {fmt(prod.precio_matriz)} €</span>}
                                                    </div>
                                                    {pc.precio_anterior != null && <div className="text-gray-400">Anterior: {fmt(pc.precio_anterior)} €</div>}
                                                    {pc.ahorro != null && parseFloat(pc.ahorro) > 0 && <div className="text-green-600 font-semibold">Ahorro: +{fmt(pc.ahorro)} €</div>}
                                                  </div>
                                                ) : <span className="text-gray-400 text-[10px]">Sin precio asignado</span>}
                                              </div>
                                            );
                                          })}
                                        </div>
                                      </div>
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tbody>
                      </table>
                    )}
                  </div>
                </div>
              )}

              {/* ══ TAB ESTADÍSTICAS ══ scroll interno */}
              {activeTab === 'estadisticas' && (
                <div className="flex-1 overflow-y-auto p-6 min-h-0">
                  {!estadisticas ? (
                    <div className="text-center py-14 text-gray-400">
                      <BarChart2 size={44} className="mx-auto mb-3 opacity-30" />
                      <p>Cargando estadísticas...</p>
                    </div>
                  ) : (
                    <div className="space-y-6">

                      {/* KPI cards */}

                      {/* Distribución — Recharts PieChart */}
                      {estadisticas.distribucion?.length > 0 && (
                        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                          <div className="p-4 border-b border-gray-100 flex items-center gap-2">
                            <BarChart2 size={16} style={{ color: PRIMARY }} />
                            <h3 className="font-semibold text-gray-800">Distribución por Rango de Ahorro</h3>
                          </div>
                          <div className="p-4">
                            <ResponsiveContainer width="100%" height={220}>
                              <BarChart data={estadisticas.distribucion} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="rango" tick={{ fontSize: 11, fill: '#6b7280' }} />
                                <YAxis tick={{ fontSize: 11, fill: '#6b7280' }} allowDecimals={false} />
                                <Tooltip content={<CustomTooltip formatter={v => `${v} productos`} />} />
                                <Bar dataKey="cantidad" name="Productos" radius={[4, 4, 0, 0]}>
                                  {estadisticas.distribucion.map((d, i) => (
                                    <Cell key={i} fill={RANGO_COLORS[d.rango] || PRIMARY} />
                                  ))}
                                </Bar>
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      )}

                      {/* Top tablas */}
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* Top por € */}
                        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                          <div className="p-4 border-b border-gray-100 flex items-center gap-2">
                            <Award size={16} style={{ color: PRIMARY }} />
                            <h3 className="font-semibold text-gray-800">Top 15 — Mayor Ahorro (€/ud)</h3>
                          </div>
                          <div className="overflow-y-auto" style={{ maxHeight: '320px' }}>
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50 sticky top-0">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Producto</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Ant.</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Act.</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Ahorro</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {estadisticas.top_ahorro.length === 0 ? (
                                  <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400 text-xs">Sin ahorros configurados</td></tr>
                                ) : estadisticas.top_ahorro.map(p => (
                                  <tr key={p.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-2">
                                      <div className="font-medium text-gray-800 truncate max-w-[160px]">{p.nombre}</div>
                                      <div className="text-xs text-gray-400 truncate">{p.proveedor}</div>
                                    </td>
                                    <td className="px-4 py-2 text-right text-gray-400 text-xs">{fmt(p.precio_anterior)}</td>
                                    <td className="px-4 py-2 text-right text-gray-700 text-xs font-medium">{fmt(p.precio)}</td>
                                    <td className="px-4 py-2 text-right">
                                      <div className="font-semibold text-green-600 text-xs">{fmt(p.ahorro)} €</div>
                                      <div className="text-[10px] text-green-400">{parseFloat(p.ahorro_pct).toFixed(2)}%</div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>

                        {/* Top por proveedor — Recharts BarChart horizontal */}
                        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                          <div className="p-4 border-b border-gray-100 flex items-center gap-2">
                            <Award size={16} style={{ color: PRIMARY }} />
                            <h3 className="font-semibold text-gray-800">Ahorro por Proveedor</h3>
                          </div>
                          <div className="p-4">
                            {estadisticas.ahorro_proveedores.length === 0 ? (
                              <div className="text-center py-10 text-gray-400 text-sm">Sin datos</div>
                            ) : (
                              <ResponsiveContainer width="100%" height={Math.max(180, estadisticas.ahorro_proveedores.length * 36)}>
                                <BarChart
                                  layout="vertical"
                                  data={estadisticas.ahorro_proveedores.map(p => ({
                                    name: p.proveedor || 'Sin proveedor',
                                    ahorro: parseFloat(p.ahorro_total) || 0,
                                  }))}
                                  margin={{ top: 4, right: 60, left: 8, bottom: 4 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                                  <XAxis type="number" tick={{ fontSize: 10, fill: '#6b7280' }}
                                    tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v.toFixed(2)} />
                                  <YAxis type="category" dataKey="name" width={110}
                                    tick={{ fontSize: 11, fill: '#374151' }} />
                                  <Tooltip content={<CustomTooltip formatter={v => `${v.toFixed(2)} €`} />} />
                                  <Bar dataKey="ahorro" name="Ahorro €" fill={PRIMARY} radius={[0, 4, 4, 0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Top por % */}
                      {estadisticas.top_ahorro_pct?.length > 0 && (
                        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                          <div className="p-4 border-b border-gray-100 flex items-center gap-2">
                            <TrendingUp size={16} style={{ color: '#6366f1' }} />
                            <h3 className="font-semibold text-gray-800">Top 10 — Mayor Ahorro (%)</h3>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500">Producto</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Anterior</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Actual</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Ahorro €</th>
                                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500">Ahorro %</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {estadisticas.top_ahorro_pct.map(p => (
                                  <tr key={p.id} className="hover:bg-gray-50">
                                    <td className="px-4 py-2">
                                      <div className="font-medium text-gray-800 truncate max-w-[260px]">{p.nombre}</div>
                                      <div className="text-xs text-gray-400">{p.proveedor}</div>
                                    </td>
                                    <td className="px-4 py-2 text-right text-xs text-gray-500">{fmt(p.precio_anterior)}</td>
                                    <td className="px-4 py-2 text-right text-xs text-gray-700 font-medium">{fmt(p.precio)}</td>
                                    <td className="px-4 py-2 text-right"><span className="text-xs font-semibold text-green-600">{fmt(p.ahorro)} €</span></td>
                                    <td className="px-4 py-2 text-right">
                                      <span className="inline-flex items-center gap-0.5 text-xs font-bold px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">
                                        <ArrowDown size={9} />{parseFloat(p.ahorro_pct).toFixed(2)}%
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {estadisticas.sin_precio_anterior > 0 && (
                        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                          <div className="flex items-start gap-3">
                            <Info size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="flex-1">
                              <p className="font-semibold text-blue-800">{estadisticas.sin_precio_anterior} producto{estadisticas.sin_precio_anterior !== 1 ? 's' : ''} sin precio anterior</p>
                              <p className="text-sm text-blue-600 mt-1">Introduce el precio que pagaba la clínica antes de CentralSalud para calcular el ahorro real.</p>
                            </div>
                            <button onClick={() => { setActiveTab('precios'); setFilterNoPrev(true); }}
                              className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg">
                              <ExternalLink size={12} />Ver pendientes
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* ══ TAB HISTÓRICO ══ layout fijo sin overflow propio */}
              {activeTab === 'historico' && (
                <div className="flex-1 flex overflow-hidden min-h-0">

                  {/* Panel lateral — clínicas + filtros */}
                  <div className="w-64 flex-shrink-0 border-r border-gray-200 bg-gray-50 flex flex-col overflow-hidden">

                    {/* ── Clínicas ── */}
                    <div className="p-4 border-b border-gray-200 flex-shrink-0">
                      <h4 className="font-semibold text-gray-700 text-sm flex items-center gap-2">
                        <Users size={15} style={{ color: PRIMARY }} />Filtrar por clínicas
                      </h4>
                      <p className="text-xs text-gray-400 mt-0.5">Elige una o varias, o deja todas</p>
                    </div>

                    {/* Lista de clínicas con scroll */}
                    <div className="overflow-y-auto p-3 space-y-1 min-h-0" style={{ maxHeight: '220px' }}>
                      <label className={`flex items-center gap-2.5 p-2.5 rounded-lg cursor-pointer transition-colors ${clientesSeleccionados.size === 0 ? 'bg-white shadow-sm border border-gray-200' : 'hover:bg-white'}`}>
                        <input type="radio" name="cfiltro" checked={clientesSeleccionados.size === 0}
                          onChange={() => setClientesSel(new Set())} className="w-3.5 h-3.5 accent-teal-500" />
                        <div>
                          <span className="text-sm font-medium text-gray-700">Todas</span>
                          <div className="text-xs text-gray-400">{clientes.length} clínicas</div>
                        </div>
                      </label>
                      <div className="text-[10px] text-gray-400 uppercase tracking-wider px-2 pt-2">O seleccionar:</div>
                      {clientes.map(c => {
                        const esMatriz = parseInt(c.clinica_matriz) === 0 || c.clinica_matriz === null;
                        const checked  = clientesSeleccionados.has(c.id);
                        return (
                          <label key={c.id}
                            className={`flex items-start gap-2.5 p-2.5 rounded-lg cursor-pointer transition-colors ${checked ? 'bg-white shadow-sm border' : 'hover:bg-white'}`}
                            style={checked ? { borderColor: PRIMARY } : {}}>
                            <input type="checkbox" checked={checked} onChange={() => handleClienteToggle(c.id)}
                              className="w-3.5 h-3.5 mt-0.5 accent-teal-500 flex-shrink-0" />
                            <div className="min-w-0">
                              <div className="flex items-center gap-1 flex-wrap">
                                <span className="text-sm font-medium text-gray-700 truncate">{c.razon_social}</span>
                                {esMatriz && <span className="text-[9px] px-1 py-0.5 rounded font-bold flex-shrink-0" style={{ backgroundColor: PRIMARY_LITE, color: PRIMARY }}>MTZ</span>}
                              </div>
                              {c.alias && <div className="text-xs text-gray-400 truncate">{c.alias}</div>}
                            </div>
                          </label>
                        );
                      })}
                    </div>

                    {/* ── Proveedor ── */}
                    <div className="border-t border-gray-200 p-3 flex-shrink-0">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider mb-2 flex items-center gap-1">
                        <Filter size={10} />Proveedor
                      </p>
                      <select value={histFiltroProveedor} onChange={e => setHistProveedor(e.target.value)}
                        className="w-full border border-gray-200 rounded-lg text-xs py-1.5 px-2 bg-white focus:outline-none text-gray-700">
                        <option value="">Todos los proveedores</option>
                        {proveedoresDisponibles.map(pv => (
                          <option key={pv} value={pv}>{pv}</option>
                        ))}
                      </select>
                    </div>

                    {/* ── Rango de fechas ── */}
                    <div className="border-t border-gray-200 p-3 flex-shrink-0 space-y-2">
                      <p className="text-[10px] text-gray-400 uppercase tracking-wider flex items-center gap-1">
                        <Calendar size={10} />Rango de fechas
                      </p>
                      <div>
                        <label className="text-[10px] text-gray-500 mb-0.5 block">Desde</label>
                        <input type="date" value={histFechaDesde} onChange={e => setHistFechaDesde(e.target.value)}
                          max={histFechaHasta || undefined}
                          className="w-full border border-gray-200 rounded-lg text-xs py-1.5 px-2 bg-white focus:outline-none text-gray-700" />
                      </div>
                      <div>
                        <label className="text-[10px] text-gray-500 mb-0.5 block">Hasta</label>
                        <input type="date" value={histFechaHasta} onChange={e => setHistFechaHasta(e.target.value)}
                          min={histFechaDesde || undefined}
                          className="w-full border border-gray-200 rounded-lg text-xs py-1.5 px-2 bg-white focus:outline-none text-gray-700" />
                      </div>
                      {(histFiltroProveedor || histFechaDesde || histFechaHasta) && (
                        <button onClick={() => { setHistProveedor(''); setHistFechaDesde(''); setHistFechaHasta(''); }}
                          className="w-full text-xs py-1 rounded-lg border transition-colors text-center"
                          style={{ borderColor: PRIMARY, color: PRIMARY }}>
                          Limpiar filtros
                        </button>
                      )}
                    </div>

                    <div className="p-3 border-t border-gray-200 flex-shrink-0 text-xs text-gray-500 flex items-center gap-1.5">
                      <Filter size={11} /><span className="truncate">{clientesFiltroTexto}</span>
                    </div>
                  </div>

                  {/* Contenido histórico — scroll propio */}
                  <div className="flex-1 overflow-y-auto p-6 min-h-0">
                    {loadingHistorico ? (
                      <div className="flex flex-col items-center justify-center h-64 gap-3">
                        <div className="w-10 h-10 border-4 border-gray-200 rounded-full animate-spin" style={{ borderTopColor: PRIMARY }} />
                        <p className="text-gray-400 text-sm">Calculando ahorro histórico...</p>
                      </div>
                    ) : !historicoData || historicoData.productos.length === 0 ? (
                      <div className="flex flex-col items-center justify-center h-64 gap-3 text-gray-400">
                        <History size={44} className="opacity-30" />
                        <p className="font-medium text-gray-500">Sin datos de ahorro histórico</p>
                        <p className="text-sm text-center max-w-xs">Necesitas productos con precio anterior configurado y pedidos realizados a un precio inferior.</p>
                      </div>
                    ) : (
                      <div className="space-y-6">

                        {/* KPIs */}
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                          {[
                            { label: 'Ahorro Total Histórico', value: fmtBigMoney(historicoData.totales.ahorro_total), sub: `desde ${fmtDate(historicoData.totales.primer_pedido)}`, icon: <TrendingDown size={20} />, color: '#059669', big: true },
                            { label: 'Unidades Compradas',     value: parseInt(historicoData.totales.total_unidades).toLocaleString('es-ES'), sub: 'con precio ant. configurado', icon: <ShoppingCart size={20} />, color: PRIMARY },
                            { label: 'Productos con Ahorro',   value: historicoData.totales.num_productos, sub: 'productos únicos', icon: <Package size={20} />, color: '#6366f1' },
                            { label: 'Ahorro Promedio/Mes',    value: fmtBigMoney(historicoData.totales.ahorro_promedio_mensual), sub: `${historicoData.por_mes.length} meses`, icon: <Calendar size={20} />, color: '#f59e0b' },
                          ].map(k => (
                            <div key={k.label} className={`border rounded-xl p-4 shadow-sm ${k.big ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200'}`}>
                              <div className="flex items-center justify-between mb-2">
                                <span className={`text-xs font-medium ${k.big ? 'text-green-700' : 'text-gray-500'}`}>{k.label}</span>
                                <div className="p-1.5 rounded-lg" style={{ backgroundColor: `${k.color}18`, color: k.color }}>{k.icon}</div>
                              </div>
                              <div className={`text-2xl font-bold ${k.big ? 'text-green-800' : 'text-gray-800'}`}>{k.value}</div>
                              <div className={`text-xs mt-0.5 ${k.big ? 'text-green-600' : 'text-gray-400'}`}>{k.sub}</div>
                            </div>
                          ))}
                        </div>

                        {/* Gráfica comparativa: gasto anterior vs gasto real */}
                        {historicoData.por_mes.length > 0 && (
                          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
                            <div className="flex items-center justify-between mb-1">
                              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                                <TrendingDown size={16} style={{ color: PRIMARY }} />
                                Comparativa de Gasto Mensual
                              </h3>
                              <div className="flex items-center gap-3 text-xs text-gray-500">
                                <span className="flex items-center gap-1.5">
                                  <span className="inline-block w-6 border-t-2 border-dashed border-red-400" />
                                  Sin CentralSalud
                                </span>
                                <span className="flex items-center gap-1.5">
                                  <span className="inline-block w-6 border-t-2 border-green-500" />
                                  Gasto real
                                </span>
                              </div>
                            </div>
                            <p className="text-xs text-gray-400 mb-4">
                              Área entre líneas = ahorro acumulado · Total ahorrado:
                              <span className="font-semibold text-green-600 ml-1">
                                {parseFloat(historicoData.totales.ahorro_total || 0).toFixed(2)} €
                              </span>
                            </p>
                            <ResponsiveContainer width="100%" height={240}>
                              <LineChart
                                data={historicoData.por_mes.map(m => ({
                                  mes:       fmtDate(m.mes),
                                  anterior:  parseFloat(m.gasto_anterior_mes) || 0,
                                  real:      parseFloat(m.gasto_real_mes)     || 0,
                                  ahorro:    parseFloat(m.ahorro_mes)         || 0,
                                  pedidos:   parseInt(m.pedidos_mes)          || 0,
                                }))}
                                margin={{ top: 8, right: 20, left: 10, bottom: 4 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="mes" tick={{ fontSize: 10, fill: '#6b7280' }} />
                                <YAxis tick={{ fontSize: 10, fill: '#6b7280' }}
                                  tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(1)}k €` : `${v.toFixed(0)} €`} />
                                <Tooltip content={({ active, payload, label }) => {
                                  if (!active || !payload?.length) return null;
                                  const d = payload[0]?.payload;
                                  return (
                                    <div className="bg-gray-800 text-white text-xs px-3 py-2.5 rounded-lg shadow-lg min-w-[170px]">
                                      <p className="font-semibold mb-2 border-b border-gray-600 pb-1">{label}</p>
                                      <p className="flex justify-between gap-4">
                                        <span className="text-red-300">Sin CentralSalud:</span>
                                        <span className="font-medium">{parseFloat(d?.anterior || 0).toFixed(2)} €</span>
                                      </p>
                                      <p className="flex justify-between gap-4">
                                        <span className="text-green-300">Gasto real:</span>
                                        <span className="font-medium">{parseFloat(d?.real || 0).toFixed(2)} €</span>
                                      </p>
                                      <p className="flex justify-between gap-4 mt-1 pt-1 border-t border-gray-600">
                                        <span style={{ color: PRIMARY }}>Ahorro mes:</span>
                                        <span className="font-bold text-green-400">+{parseFloat(d?.ahorro || 0).toFixed(2)} €</span>
                                      </p>
                                      <p className="text-gray-400 mt-1">{d?.pedidos} pedidos</p>
                                    </div>
                                  );
                                }} />
                                <Line
                                  type="monotone" dataKey="anterior" name="Sin CentralSalud"
                                  stroke="#f87171" strokeWidth={2} strokeDasharray="5 3"
                                  dot={{ r: 3, fill: '#f87171' }} activeDot={{ r: 5 }} />
                                <Line
                                  type="monotone" dataKey="real" name="Gasto real"
                                  stroke="#22c55e" strokeWidth={2.5}
                                  dot={{ r: 3, fill: '#22c55e' }} activeDot={{ r: 5 }} />
                              </LineChart>
                            </ResponsiveContainer>
                          </div>
                        )}

                        {/* Evolución mensual del ahorro — Recharts BarChart */}
                        {historicoData.por_mes.length > 1 && (
                          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
                            <div className="flex items-center justify-between mb-4">
                              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                                <Calendar size={16} style={{ color: PRIMARY }} />Ahorro Mensual
                              </h3>
                              <span className="text-xs text-gray-400">{historicoData.por_mes.length} meses</span>
                            </div>
                            <ResponsiveContainer width="100%" height={180}>
                              <BarChart
                                data={historicoData.por_mes.map(m => ({
                                  mes: fmtDate(m.mes),
                                  ahorro: parseFloat(m.ahorro_mes) || 0,
                                  pedidos: parseInt(m.pedidos_mes) || 0,
                                }))}
                                margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                <XAxis dataKey="mes" tick={{ fontSize: 10, fill: '#6b7280' }} />
                                <YAxis tick={{ fontSize: 10, fill: '#6b7280' }} tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v.toFixed(0)} />
                                <Tooltip content={({ active, payload, label }) => {
                                  if (!active || !payload?.length) return null;
                                  return (
                                    <div className="bg-gray-800 text-white text-xs px-3 py-2 rounded-lg shadow-lg">
                                      <p className="font-semibold mb-1">{label}</p>
                                      <p style={{ color: PRIMARY }}>Ahorro: {parseFloat(payload[0]?.value || 0).toFixed(2)} €</p>
                                      <p className="text-gray-300">{payload[0]?.payload?.pedidos} pedidos</p>
                                    </div>
                                  );
                                }} />
                                <Bar dataKey="ahorro" name="Ahorro €" fill={PRIMARY} radius={[4, 4, 0, 0]} />
                              </BarChart>
                            </ResponsiveContainer>
                          </div>
                        )}

                        {/* Ahorro por clínica — Recharts BarChart horizontal */}
                        {historicoData.por_cliente.length > 1 && (
                          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                            <div className="p-4 border-b border-gray-100 flex items-center gap-2">
                              <Users size={16} style={{ color: PRIMARY }} />
                              <h3 className="font-semibold text-gray-800">Ahorro por Clínica</h3>
                            </div>
                            <div className="p-4">
                              <ResponsiveContainer width="100%" height={Math.max(160, historicoData.por_cliente.length * 44)}>
                                <BarChart
                                  layout="vertical"
                                  data={historicoData.por_cliente.map(c => ({
                                    name: c.alias || c.razon_social,
                                    ahorro: parseFloat(c.ahorro_total) || 0,
                                    pedidos: c.num_pedidos,
                                  }))}
                                  margin={{ top: 4, right: 60, left: 8, bottom: 4 }}>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={false} />
                                  <XAxis type="number" tick={{ fontSize: 10, fill: '#6b7280' }}
                                    tickFormatter={v => v >= 1000 ? `${(v/1000).toFixed(1)}k` : v.toFixed(2)} />
                                  <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: '#374151' }} />
                                  <Tooltip content={({ active, payload, label }) => {
                                    if (!active || !payload?.length) return null;
                                    return (
                                      <div className="bg-gray-800 text-white text-xs px-3 py-2 rounded-lg shadow-lg">
                                        <p className="font-semibold mb-1">{label}</p>
                                        <p style={{ color: PRIMARY }}>Ahorro: {parseFloat(payload[0]?.value || 0).toFixed(2)} €</p>
                                        <p className="text-gray-300">{payload[0]?.payload?.pedidos} pedidos</p>
                                      </div>
                                    );
                                  }} />
                                  <Bar dataKey="ahorro" name="Ahorro €" fill={PRIMARY} radius={[0, 4, 4, 0]} />
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                          </div>
                        )}

                        {/* Tabla productos histórico */}
                        <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                          <div className="p-4 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
                            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                              <Package size={16} style={{ color: PRIMARY }} />
                              Detalle por Producto
                              <span className="text-xs text-gray-400 font-normal">({histProductosFiltrados.length})</span>
                            </h3>
                            <div className="flex items-center gap-2 flex-wrap">
                              <SearchInput
                                size="sm"
                                className="w-40"
                                borderClass="border border-gray-200"
                                blurColor="#e5e7eb"
                                value={histSearch}
                                onChange={e => setHistSearch(e.target.value)}
                                placeholder="Buscar..."
                              />
                              <select value={histOrden} onChange={e => setHistOrden(e.target.value)}
                                className="border border-gray-200 rounded-lg text-xs py-1.5 px-2 focus:outline-none text-gray-600">
                                <option value="ahorro_total">Ordenar: Ahorro €</option>
                                <option value="ahorro_pct">Ordenar: Ahorro %</option>
                                <option value="total_unidades">Ordenar: Unidades</option>
                                <option value="num_pedidos">Ordenar: Pedidos</option>
                              </select>
                            </div>
                          </div>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm min-w-[860px]">
                              <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wide">Producto</th>
                                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">Precio ant.</th>
                                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">Precio medio pagado</th>
                                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">Uds.</th>
                                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">Ahorro Total €</th>
                                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wide">Ahorro %</th>
                                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wide">Pedidos</th>
                                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wide">Período</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {histProductosFiltrados.length === 0 ? (
                                  <tr><td colSpan={8} className="px-4 py-12 text-center text-gray-400">
                                    <Package size={32} className="mx-auto mb-2 opacity-30" /><p>Sin productos</p>
                                  </td></tr>
                                ) : histProductosFiltrados.map((p, idx) => (
                                  <tr key={p.producto_id} className={`hover:bg-gray-50 transition-colors ${idx % 2 === 0 ? '' : 'bg-gray-50/30'}`}>
                                    <td className="px-4 py-3">
                                      <div className="font-medium text-gray-800 leading-tight">{p.nombre}</div>
                                      <div className="text-xs text-gray-400 flex items-center gap-1.5 mt-0.5">
                                        {p.ref_proveedor && <span>{p.ref_proveedor}</span>}
                                        {p.proveedor && <><span className="text-gray-300">·</span><span>{p.proveedor}</span></>}
                                      </div>
                                    </td>
                                    <td className="px-4 py-3 text-right text-gray-500 text-xs">{fmt(p.precio_anterior)} €</td>
                                    <td className="px-4 py-3 text-right text-gray-700 text-xs font-medium">{fmt(p.precio_promedio_pagado)} €</td>
                                    <td className="px-4 py-3 text-right font-semibold text-gray-800">{parseInt(p.total_unidades).toLocaleString('es-ES')}</td>
                                    <td className="px-4 py-3 text-right">
                                      <div className="font-bold text-green-600">{parseFloat(p.ahorro_total).toFixed(2)} €</div>
                                      <div className="text-xs text-green-400">{fmt(p.ahorro_por_unidad)} €/ud</div>
                                    </td>
                                    <td className="px-4 py-3 text-right">
                                      <span className="inline-flex items-center gap-0.5 text-xs font-bold px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                                        <ArrowDown size={9} />{parseFloat(p.ahorro_pct).toFixed(2)}%
                                      </span>
                                    </td>
                                    <td className="px-4 py-3 text-center text-xs text-gray-600">{p.num_pedidos}</td>
                                    <td className="px-4 py-3 text-center text-xs text-gray-400">
                                      <div>{fmtDate(p.primer_pedido?.substring(0, 7))}</div>
                                      <div className="text-gray-300">↓</div>
                                      <div>{fmtDate(p.ultimo_pedido?.substring(0, 7))}</div>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                              {histProductosFiltrados.length > 0 && (
                                <tfoot className="bg-gray-50 border-t-2 border-gray-200">
                                  <tr>
                                    <td className="px-4 py-3 font-bold text-gray-700 text-sm" colSpan={3}>
                                      TOTAL ({histProductosFiltrados.length} productos)
                                    </td>
                                    <td className="px-4 py-3 text-right font-bold text-gray-800">
                                      {histProductosFiltrados.reduce((s, p) => s + parseInt(p.total_unidades || 0), 0).toLocaleString('es-ES')}
                                    </td>
                                    <td className="px-4 py-3 text-right font-bold text-green-600 text-base">
                                      {histProductosFiltrados.reduce((s, p) => s + parseFloat(p.ahorro_total || 0), 0).toFixed(2)} €
                                    </td>
                                    <td colSpan={3} className="px-4 py-3 text-xs text-gray-400 text-center">
                                      {clientesFiltroTexto}
                                    </td>
                                  </tr>
                                </tfoot>
                              )}
                            </table>
                          </div>
                        </div>

                      </div>
                    )}
                  </div>
                </div>
              )}

            </div>{/* fin card tabs */}
          </div>
        )}
      </div>{/* fin área principal */}
    </div>
  );
}